import { useCallback } from "react";
import { graphql } from "../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import Button from "../../../ui/Button/Button";
import urls from "../../../routes";
import Page from "../../Page";
import styles from "./Wordpress.module.scss";
import Card from "../../../ui/Card/Card";
import SettingsSection from "../../Settings/components/SettingsSection";
import Select from "../../../ui/Select/Select";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import Header from "../../Settings/components/Header/Header";
import { zodResolver } from "@hookform/resolvers/zod";
import { WordpressPostStatus } from "../../../__generated__/gql/graphql";
import Badge from "../../../ui/Badge/Badge";
import { Wordpress as WordpressLogo } from "../../../ui/LogoIcon/LogoIcon";
import SetupWordpressModal from "./SetupWordpressModal";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../ui/Toast/ToastContext";

const WordpressConnectionQuery = graphql(/* GraphQL */ `
  query wordpressConnection {
    wordpressConnection {
      id
      url
      connected
      settings {
        postStatus
        author
      }
    }
  }
`);

const WordpressAuthorsQuery = graphql(/* GraphQL */ `
  query wordpressAuthors {
    wordpressAuthors {
      id
      name
    }
  }
`);

const UpdateWordpressConnectionSettingsMutation = graphql(/* GraphQL */ `
  mutation updateWordpressConnectionSettings(
    $input: UpdateWordpressConnectionSettingsInput!
  ) {
    updateWordpressConnectionSettings(input: $input) {
      id
      settings {
        postStatus
        author
      }
    }
  }
`);

const DeleteWordpressConnectionMutation = graphql(/* GraphQL */ `
  mutation deleteWordpressConnection {
    deleteWordpressConnection {
      id
    }
  }
`);

const schema = z.object({
  postStatus: z.enum([WordpressPostStatus.Draft, WordpressPostStatus.Publish]),
  author: z.string().nullable().optional(),
});

type WordpressFormData = z.infer<typeof schema>;

function WordpressForm({
  connected,
  url,
  defaultValues,
}: {
  connected: boolean;
  url: string;
  defaultValues?: WordpressFormData;
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [, updateWordpressConnectionSettings] = useMutation(
    UpdateWordpressConnectionSettingsMutation,
  );
  const [, deleteWordpressConnection] = useMutation(
    DeleteWordpressConnectionMutation,
  );
  const [wordpressAuthorsResponse] = useQuery({ query: WordpressAuthorsQuery });

  const {
    control,
    formState: { isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = useForm<WordpressFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues,
  });

  const onSubmit = useCallback(
    async (data: WordpressFormData) => {
      await updateWordpressConnectionSettings({
        input: {
          postStatus: data.postStatus,
          author: data.author,
        },
      });

      if (!connected) {
        showToast({
          variant: "success",
          title: "WordPress connected",
        });
      }

      reset(data);
    },
    [connected, reset, showToast, updateWordpressConnectionSettings],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Header
        title="WordPress"
        titleElement={
          connected ? (
            <Badge color="success">Connected</Badge>
          ) : (
            <Badge color="default">Not Connected</Badge>
          )
        }
        description="Setup publishing to WordPress"
        isSubmitting={isSubmitting}
        saveButtonText={connected ? "Save" : "Connect"}
        isDirty={isDirty || !connected}
        onCancel={async () => {
          if (!connected) {
            await deleteWordpressConnection({});
            await navigate(urls.publishing.connections.root);
          } else {
            reset();
          }
        }}
      />
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          marginBottom: 20,
          paddingTop: 24,
          borderTop: "1px solid var(--color-gray-200)",
        }}
      >
        <>
          <Badge size="large" variant="badge" showDot={false}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <WordpressLogo size="medium" />
              <span style={{ fontWeight: 600, color: "var(--color-gray-800)" }}>
                {url.replace("https://", "").replace("http://", "")}
              </span>
            </div>
          </Badge>
          <Button
            variant="link"
            onClick={async () => {
              await deleteWordpressConnection({});
              await navigate(urls.publishing.connections.root);
            }}
          >
            Disconnect
          </Button>
        </>
      </div>

      <SettingsSection
        label="Status"
        description="The status of articles published by Macaw"
      >
        <div className={styles.select}>
          <Controller
            control={control}
            name="postStatus"
            render={({ field }) => (
              <Select
                data={[
                  {
                    label: "Draft",
                    value: WordpressPostStatus.Draft,
                  },
                  {
                    label: "Publish",
                    value: WordpressPostStatus.Publish,
                  },
                ]}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </SettingsSection>

      <SettingsSection
        label="Author"
        description="The author you would like articles to be published from"
      >
        <div className={styles.select}>
          <Controller
            control={control}
            name="author"
            render={({ field }) => (
              <Select
                clearable
                data={
                  wordpressAuthorsResponse.data?.wordpressAuthors.map(
                    (author) => ({ value: author.id, label: author.name }),
                  ) ?? []
                }
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </SettingsSection>
    </form>
  );
}

function Wordpress() {
  const [wordpressConnectionResponse] = useQuery({
    query: WordpressConnectionQuery,
  });
  const navigate = useNavigate();

  return (
    <>
      <SetupWordpressModal
        onClose={() => navigate(urls.publishing.connections.root)}
        isOpen={
          !wordpressConnectionResponse.fetching &&
          !wordpressConnectionResponse.data?.wordpressConnection?.url
        }
      />
      <Page
        loading={
          wordpressConnectionResponse.fetching ||
          !wordpressConnectionResponse.data?.wordpressConnection?.url
        }
        title="WordPress"
        documentTitle="WordPress setup"
        breadcrumbs={[
          { url: urls.publishing.connections.root, name: "Publishing" },
        ]}
      >
        <div className={styles.container}>
          <Card className={styles.card}>
            {!wordpressConnectionResponse.fetching &&
            wordpressConnectionResponse.data?.wordpressConnection?.url ? (
              <WordpressForm
                connected={
                  !!wordpressConnectionResponse.data?.wordpressConnection
                    ?.connected
                }
                url={wordpressConnectionResponse.data?.wordpressConnection?.url}
                defaultValues={{
                  author:
                    wordpressConnectionResponse.data?.wordpressConnection
                      .settings?.author ?? undefined,
                  postStatus:
                    wordpressConnectionResponse.data?.wordpressConnection
                      ?.settings?.postStatus ?? WordpressPostStatus.Draft,
                }}
              />
            ) : null}
          </Card>
        </div>
      </Page>
    </>
  );
}

export default Wordpress;
